import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from "react-helmet";
import { Link as RouterLink, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, FileDownload, Settings, InfoOutlined } from '@mui/icons-material';
import { Tabs, Tab, Button, Grid, Box, Tooltip } from '@mui/material';
import BreadCrumbs from '../BreadCrumbs/index.js';
import APIUtils from '../../utils/APIUtils.js';
import SessionConstants from '../../config/SessionConstants.js';
import Loading from '../Loading/index.js';
import PatientDetailsSelectDataView from './PatientDetailsSelectDataView/index.js';
import PatientDetailsMolecularTable from './PatientDetailsMolecularTable/index.js';
import PatientDetailsBiochemicalTestResultSelector from './PatientDetailsBiochemicalTestResultSelector/index.js';
import PatientDetailsBiochemicalSummaryPanel from './PatientDetailsBiochemicalSummaryPanel/index.js';
import PatientDetailsBiochemical from './PatientDetailsBiochemical/index.js';
import PatientDetailsScatterPlotSelector from './PatientDetailsScatterPlotSelector/index.js';
import PatientDetailsSingleAnalyteAnalysisSelector from './PatientDetailsSingleAnalyteAnalysisSelector/index.js';
import TabPanel from '../../uiComponents/MuiTabs/MuiTabPanel.js';
import muiTabStyles from '../../uiComponents/MuiTabs/MuiTabsStyles.js';
import muiInputStyles from '../../uiComponents/MuiInput/MuiInputStyles.js';
import ScatterplotChart from '../ScatterPlotChart/index.js';
import HistogramChart from '../HistogramChart/index.js';
import AnalyteGroup from '../../config/AnalyteGroup.js';
import PlotOptionsDialog from '../PlotOptionsDialog/index.js';

const PatientDetails = () => {
  const muiTabClasses = muiTabStyles();
  const muiInputClasses = muiInputStyles();
  const location = useLocation();
  const ed3nConfig = __ED3N_CONFIG__;
  const { patientId } = useParams();

  // Destructured from navigation state
  const {
    programId: navProgramId,
    variantName,
    breadCrumbs: navBreadCrumbs,
    potentialConditionsString
  } = location.state || {};

  // Use navigation state as initial values if you want to update them
  const [breadCrumbs] = useState(navBreadCrumbs
    ? [...navBreadCrumbs, { name: 'PatientDetails', patientId, programId: navProgramId, potentialConditionsString }]
    : []);
  const [programId] = useState(navProgramId); // If you don't need to update, just use navProgramId directly
  const [variants, setVariants] = useState([]);
  // Current user's interpretations keyed by variantId; supplies Quick Actions assigned status + ids.
  const [assignedByVariantId, setAssignedByVariantId] = useState({});
  const [biochemicalData, setBiochemicalData] = useState([]);
  const [biochemicalStatistics, setBiochemicalStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [biochemicalLoading, setBiochemicalLoading] = useState(false);
  const [scatterplotLoading, setScatterplotLoading] = useState(false);
  const [histogramLoading, setHistogramLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(1);
  const [hasInitialBiochemicalData, setHasInitialBiochemicalData] = useState(false);
  const [selectedDataView, setSelectedDataView] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);
  const [selectedAnalyte, setSelectedAnalyte] = useState(null);
  const [scatterplotData, setScatterplotData] = useState(null);
  const [showScatterplotChart, setShowScatterplotChart] = useState(false);
  const scatterplotRef = useRef();
  const scatterplotSubtitle = `For patients flagged as potential ${potentialConditionsString?.replace(/,/g, ', ')}`;
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedAnalyteToPlot, setSelectedAnalyteToPlot] = useState(null);
  const [showHistogramChart, setShowHistogramChart] = useState(false);
  const [histogramData, setHistogramData] = useState(null);
  const histogramRef = useRef();
  const [selectedScatterplotOption, setSelectedScatterplotOption] = useState(null);
  const [scatterplotPlotOptionsOpen, setScatterplotPlotOptionsOpen] = useState(false);
  const [scatterplotDataScope, setScatterplotDataScope] = useState('my_program');
  const [histogramPlotOptionsOpen, setHistogramPlotOptionsOpen] = useState(false);
  const [histogramDataScope, setHistogramDataScope] = useState('my_program');
  const [showHistogramCutoffLines, setShowHistogramCutoffLines] = useState(false);
  const [showScatterplotCutoffLines, setShowScatterplotCutoffLines] = useState(false);
  const [showScatterplotRegressionLine, setShowScatterplotRegressionLine] = useState(false);
  const [showScatterplotDensityCloud, setShowScatterplotDensityCloud] = useState(false);

  useEffect(() => {
    fetchPatientData(programId, patientId, true); // true = initial load
  }, [programId, patientId]);

  useEffect(() => {
    const loadAssignedInterpretations = async () => {
      try {
        const assigned = await APIUtils.getInterpretationsAssignedToUser();
        const lookup = (Array.isArray(assigned) ? assigned : []).reduce((map, item) => {
          const variantId = item?.variant?.variantObjectId;
          if (variantId) {
            map[variantId] = {
              status: item.interpretation?.interpretationStatus,
              variantReviewId: item.interpretation?.variantReviewId,
              variantInterpretationId: item.interpretation?.variantInterpretationId,
            };
          }
          return map;
        }, {});
        setAssignedByVariantId(lookup);
      } catch (error) {
        console.error('Error getting interpretations assigned to user');
      }
    };

    if (sessionStorage.getItem(SessionConstants.USER_EMAIL)) {
      loadAssignedInterpretations();
    }
  }, []);

  const fetchPatientData = async (programId, patientId, isInitial = false) => {
    setLoading(true);
    try {
      const [
        variantsResult,
        biochemicalPatientResult,
        biochemicalStatisticsResult
      ] = await Promise.allSettled([
        APIUtils.getVariantsByProgramPatientId(programId, patientId),
        APIUtils.getBiochemicalDataByProgramPatientId(programId, patientId),
        APIUtils.getBiochemicalStatistics(programId, null) // Get user's program data for all analytes
      ]);

      // Variants
      if (variantsResult.status === "fulfilled" && variantsResult.value && !variantsResult.value.error) {
        setVariants(variantsResult.value);
        // console.log("Variants: ", variantsResult.value);
      } else {
        setVariants([]);
        console.warn("No variants found for this patient.");
      }

      // Biochemical Data
      if (biochemicalPatientResult.status === "fulfilled" && biochemicalPatientResult.value && !biochemicalPatientResult.value.error) {
        setBiochemicalData(biochemicalPatientResult.value);

        // Set flag only on initial load
        if (isInitial) {
          setHasInitialBiochemicalData(Object.keys(biochemicalPatientResult.value).length > 0);
        }
      } else {
        setBiochemicalData([]);
        if (isInitial) setHasInitialBiochemicalData(false);
        console.warn("No biochemical data found for this patient.");
      }

      // Biochemical Statistics
      if (biochemicalStatisticsResult.status === "fulfilled" && biochemicalStatisticsResult.value && !biochemicalStatisticsResult.value.error) {
        setBiochemicalStatistics(biochemicalStatisticsResult.value);
        // console.log("Biochemical Statistics: ", biochemicalStatisticsResult.value);
      } else {
        setBiochemicalStatistics([]);
        console.warn("No biochemical statistics found.");
      }
    } catch (error) {
      // This catch is only for unexpected errors, not for 404s, etc.
      setVariants([]);
      setBiochemicalData([]);
      setBiochemicalStatistics([]);
      if (isInitial) setHasInitialBiochemicalData(false);
      console.error("Unexpected error in fetchPatientData:", error);
    } finally {
      setLoading(false);
    }
  };

  const uniqueCategories = Array.from(
    new Set((biochemicalData.analyteData || []).map(item => item.analyteCategory))
  );
  const categoryOptions = uniqueCategories
    .filter(cat => AnalyteGroup[cat]) // Only include categories defined in AnalyteGroup.js
    .map(cat => ({
      label: AnalyteGroup[cat],
      value: cat
    }));

    const statsByCode = {};
  (biochemicalStatistics.statistics || []).forEach(stat => {
    if (stat.analyte_code && stat.descAndShortName) {
      statsByCode[stat.analyte_code] = stat.descAndShortName;
    }
  });

  const analyteOptions = (biochemicalData.analyteData || [])
    .filter(item => statsByCode[item.code])
    .map(item => ({
      label: statsByCode[item.code], // descAndShortName from statistics
      value: item.code,
      analyteCategory: item.analyteCategory
    }));

  const filteredAnalyteOptions = selectedCategory
  ? analyteOptions.filter(opt => opt.analyteCategory === selectedCategory)
  : [];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const fetchBiochemicalData = async (programId, patientId, testId) => {
    setBiochemicalLoading(true);
    try {
      const result = await APIUtils.getBiochemicalDataByProgramPatientId(programId, patientId, testId);
      if (result && !result.error) {
        setBiochemicalData(result);
        // update selectedValue to the active test result
        const activeTest = result.testResultViews?.find(view => view.active);
        setSelectedValue(activeTest ? {
          label: activeTest.testName,
          testId: activeTest.testId,
          active: activeTest.active,
        } : null);
      }
    } catch (error) {
      setBiochemicalData([]);
      console.warn("No biochemical data found for this patient.");
    } finally {
      setBiochemicalLoading(false);
    }
  };

  const handleTestResultSelect = (event, newValue) => {
    // Prevent API call if the same testId is selected
    if (!newValue || newValue.testId === selectedValue?.testId) return;
    setSelectedValue(newValue);
    fetchBiochemicalData(programId, patientId, newValue.testId);
    
    // Clear chart data when test result changes
    setSelectedScatterplotOption(null);
    setShowScatterplotChart(false);
    setScatterplotData(null);
    setSelectedCategory('');
    setSelectedAnalyteToPlot(null);
    setShowHistogramChart(false);
    setHistogramData(null);
  };

  // Function to fetch statistics for Plot Options (only updates chart, not table)
  const handleFetchPlotOptionsData = async (programIds, analyteCode = null) => {
    const response = await APIUtils.getBiochemicalStatistics(programIds, analyteCode);
    return response;
  };

  const options = useMemo(() => {
    if (!biochemicalData?.testResultViews) return [];
    return biochemicalData.testResultViews.map(view => ({
      label: view.testName,
      testId: view.testId,
      active: view.active,
    }));
  }, [biochemicalData]);

  const handleScatterplotSubmit = async (selectedOption, overrideDataScope = null) => {
    if (!selectedOption) return;
    const [xAxis, yAxis] = selectedOption.codeString.split('|');
    const yAxisType = selectedOption.yAxisType;

    // Determine which programId to use based on data scope. A normal Submit
    // (no override) always resets to my_program, so use that as the effective
    // scope here; Plot Options supplies its scope explicitly via overrideDataScope.
    const effectiveDataScope = overrideDataScope !== null ? overrideDataScope : 'my_program';
    const targetProgramId = effectiveDataScope === 'all_programs' ? 'ALL' : programId;

    const axisOptions = {
      xAxis,
      yAxis,
      yAxisType,
      programId: targetProgramId,
      patientId,
      testId: selectedValue?.testId || 0
    };

    setScatterplotLoading(true);
    try {
      const response = await APIUtils.biochemicalScatterplot(axisOptions);
      // If called from Submit button (not Plot Options), reset to default scope and
      // view toggles now — after the fetch — so the previous chart (e.g. density cloud)
      // stays on screen behind the spinner until the new data is ready to render.
      if (overrideDataScope === null) {
        setScatterplotDataScope('my_program');
        setShowScatterplotCutoffLines(false);
        setShowScatterplotRegressionLine(false);
        setShowScatterplotDensityCloud(false);
      }
      setScatterplotData(response);
      // console.log("Scatterplot Data Response:", JSON.stringify(response));
      // console.table((response?.dataPoints || []).map((d, i) => ({ index: i, x: d.x, y: d.y, highlight: d.highlight })));
    } catch (error) {
      // Set data to null to show "No Data Available"
      setScatterplotData(null);
      console.error('Error fetching scatterplot data:', error);
    } finally {
      setScatterplotLoading(false);
      setShowScatterplotChart(true); // Only reveal chart area after load completes
    }
  };

  const handleSingleAnalyteAnalysisSubmit = async (settings, overrideDataScope = null) => {
    if (!settings.analyte) return;

    // If called from Submit button (not Plot Options), reset to default scope and cutoff lines
    if (overrideDataScope === null) {
      setHistogramDataScope('my_program');
      setShowHistogramCutoffLines(false);
    }

    // Determine which programId to use based on data scope
    const effectiveDataScope = overrideDataScope !== null ? overrideDataScope : histogramDataScope;
    const targetProgramId = effectiveDataScope === 'all_programs' ? 'ALL_PROGRAMS' : programId;

    const histogramOptions = {
      analyte: settings.analyte.value,
      programId: targetProgramId,
      patientId
    };
    setHistogramLoading(true);
    setShowHistogramChart(true); // Set to true before API call
    try {
      const response = await APIUtils.biochemicalHistogram(histogramOptions);
      setHistogramData(response);
      // console.log("Histogram Data Response:", JSON.stringify(response));
    } catch (error) {
      // Keep chart visible but set data to null to show "No Data Available"
      setHistogramData(null);
      console.error('Error fetching histogram data:', error);
    } finally {
      setHistogramLoading(false);
    }
  };

  // Scatterplot plot options handlers
  const handleScatterplotPlotOptionsSave = async (options) => {
    const { dataScope: newDataScope, testResult: newTestResult, showCutoffLines, showRegressionLine, showDensityCloud } = options;
    setScatterplotDataScope(newDataScope);
    setShowScatterplotCutoffLines(showCutoffLines);
    setShowScatterplotRegressionLine(showRegressionLine);
    setShowScatterplotDensityCloud(showDensityCloud);
    
    // If test result changed, just update the selected value (don't trigger full biochemical load)
    if (newTestResult && newTestResult.testId !== selectedValue?.testId) {
      setSelectedValue(newTestResult);
    }
    
    setScatterplotPlotOptionsOpen(false);
    // Re-fetch chart data with new scope or test result
    if (selectedScatterplotOption) {
      await handleScatterplotSubmit(selectedScatterplotOption, newDataScope);
    }
  };

  // Histogram plot options handlers
  const handleHistogramPlotOptionsSave = async (options) => {
    const { dataScope: newDataScope, showCutoffLines } = options;
    setHistogramDataScope(newDataScope);
    setShowHistogramCutoffLines(showCutoffLines);
    setHistogramPlotOptionsOpen(false);
    // Re-fetch data with new scope (pass newDataScope directly to avoid stale state)
    if (selectedAnalyteToPlot) {
      await handleSingleAnalyteAnalysisSubmit({ analyte: selectedAnalyteToPlot }, newDataScope);
    }
  };

  // Clean histogram title by removing trailing "for Program..." or "for program..." 
  const cleanHistogramTitle = useMemo(() => {
    if (!histogramData?.histogramTitle) return 'Selected Analyte';
    // Remove "for Program <name>" or "for program <name>" at the end of the string
    return histogramData.histogramTitle.replace(/\s+for\s+[Pp]rogram\s+\S+$/i, '').trim();
  }, [histogramData?.histogramTitle]);

  // Check if cutoff data is available (at least one non-null cutoff value) for histogram
  const hasCutoffData = useMemo(() => {
    if (!histogramData?.cutoffValues) return false;
    return (
      (histogramData.cutoffValues.normalFloor !== undefined && histogramData.cutoffValues.normalFloor !== null) ||
      (histogramData.cutoffValues.normalCeiling !== undefined && histogramData.cutoffValues.normalCeiling !== null)
    );
  }, [histogramData?.cutoffValues]);

  // Check if cutoff data is available for scatterplot (either X or Y axis, and data points exist)
  const hasScatterplotCutoffData = useMemo(() => {
    if (!scatterplotData?.dataPoints?.length) return false;
    const xCutoffs = scatterplotData?.xAxisConfig?.cutoffValues;
    const yCutoffs = scatterplotData?.yAxisConfig?.cutoffValues;
    
    const hasXCutoffs = xCutoffs && (
      (xCutoffs.normalFloor !== undefined && xCutoffs.normalFloor !== null) ||
      (xCutoffs.normalCeiling !== undefined && xCutoffs.normalCeiling !== null)
    );
    
    const hasYCutoffs = yCutoffs && (
      (yCutoffs.normalFloor !== undefined && yCutoffs.normalFloor !== null) ||
      (yCutoffs.normalCeiling !== undefined && yCutoffs.normalCeiling !== null)
    );
    
    return hasXCutoffs || hasYCutoffs;
  }, [scatterplotData?.dataPoints, scatterplotData?.xAxisConfig?.cutoffValues, scatterplotData?.yAxisConfig?.cutoffValues]);

  // Get cutoff disabled message — used when no data points are present
  const scatterplotCutoffDisabledMessage = useMemo(() => {
    if (hasScatterplotCutoffData) return '';
    return 'No data available for cutoff lines';
  }, [hasScatterplotCutoffData]);

  // Check if regression data is available for scatterplot
  const hasScatterplotRegressionData = useMemo(() => {
    return scatterplotData?.regressionOutput != null;
  }, [scatterplotData?.regressionOutput]);

  // Get regression disabled message from outputMessages (WARNING level) or use default
  const scatterplotRegressionDisabledMessage = useMemo(() => {
    if (hasScatterplotRegressionData) return '';
    const warnings = scatterplotData?.outputMessages?.filter(msg => msg.messageLevel === 'WARNING') || [];
    if (warnings.length > 0) return warnings[0].messageContent;
    return 'No regression data available';
  }, [scatterplotData?.outputMessages, hasScatterplotRegressionData]);

  // Check if the scatterplot has enough data to compute a population density
  // cloud. The cloud is now derived on the UI from the raw data points via
  // d3.contourDensity (the endpoint's densityContours array is ignored), so a
  // handful of points is all that's required.
  const hasScatterplotDensityData = useMemo(() => {
    return (scatterplotData?.dataPoints?.length ?? 0) >= 2;
  }, [scatterplotData?.dataPoints]);

  // Density cloud disabled message shown by the info icon when no density data
  const scatterplotDensityDisabledMessage = useMemo(() => {
    if (hasScatterplotDensityData) return '';
    return 'No population density data available';
  }, [hasScatterplotDensityData]);

  // Build display title for scatterplot export (includes patient and program info)
  const scatterplotDisplayTitle = useMemo(() => {
    let title = scatterplotData?.plotProperties?.plotTitle || 'Scatterplot Chart';
    if (patientId) {
      title += ` for Patient ${patientId}`;
    }
    if (scatterplotDataScope === 'my_program') {
      title += ` in Program ${programId}`;
    } else if (scatterplotDataScope === 'all_programs') {
      title += ' in All Programs';
    }
    return title;
  }, [scatterplotData?.plotProperties?.plotTitle, patientId, programId, scatterplotDataScope]);

  const canExportScatterplot = showScatterplotChart && (scatterplotData?.dataPoints?.length ?? 0) > 0;

  return (
    <div>
      <Loading isLoading={loading || biochemicalLoading} />
      <Helmet>
        <title>ED3N - Patient Details</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">Patient ID: {patientId}</h1>
      <div className="button-left-right-bar">
        <div>
          <RouterLink to="/programsummary">
            <Button
              id="backButton"
              aria-label="Go Back to Program Summary"
              startIcon={<ChevronLeft />}
              variant="contained"
              color="primary"
            >
              Back to Program Summary
            </Button>
          </RouterLink>
        </div>
      </div>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        centered
        className={muiTabClasses.tabs}
      >
        <Tab label="Biochemical" id="tab-0" aria-controls="tabpanel-0" className={muiTabClasses.tab} />
        <Tab label="Molecular" id="tab-1" aria-controls="tabpanel-1" className={muiTabClasses.tab} />
        <Tab label="Clinical" id="tab-2" aria-controls="tabpanel-2" className={muiTabClasses.tab} />
      </Tabs>
      <TabPanel value={selectedTab} index={0}>
        {loading ? null : (
          (ed3nConfig.disableBiochemical !== true ?
            (hasInitialBiochemicalData ? 
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <PatientDetailsBiochemicalSummaryPanel
                  biochemicalData={biochemicalData}
                  potentialConditionsString={potentialConditionsString}
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                {/* Test Result selector is always the first selection */}
                <Box sx={{ mb: 2 }}>
                  {options && options.length > 0 ? (
                    <PatientDetailsBiochemicalTestResultSelector
                      classes={muiInputClasses}
                      options={options}
                      selectedValue={selectedValue}
                      handleTestResultSelect={handleTestResultSelect}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      No patient biochemical data
                    </Box>
                  )}
                </Box>
                {/* Data View selector shown after a test result is selected */}
                {(() => {
                  const hasSelectedTestResult = Boolean(selectedValue);
                  return (
                    <>
                {hasSelectedTestResult && (
                  <Box sx={{ mb: 2 }}>
                    <PatientDetailsSelectDataView
                      selectedDataView={selectedDataView}
                      setSelectedDataView={setSelectedDataView}
                      classes={muiInputClasses}
                    />
                  </Box>
                )}
                {hasSelectedTestResult && selectedDataView === 'biochemical' && !biochemicalLoading && (
                  <PatientDetailsBiochemical
                    classes={muiInputClasses}
                    muiTabClasses={muiTabClasses}
                    biochemicalData={biochemicalData}
                    biochemicalStatistics={biochemicalStatistics}
                    selectedValue={selectedValue}
                    selectedAnalyte={selectedAnalyte}
                    setSelectedAnalyte={setSelectedAnalyte}
                    programId={programId}
                    patientId={patientId}
                    onFetchPlotOptionsData={handleFetchPlotOptionsData}
                  />
                )}
                {hasSelectedTestResult && selectedDataView === 'disease_risk_scatterplot' && !biochemicalLoading && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <PatientDetailsScatterPlotSelector
                        classes={muiInputClasses}
                        scatterplotOptions={biochemicalData.scatterplotOptions || []}
                        selectedOption={selectedScatterplotOption}
                        setSelectedOption={setSelectedScatterplotOption}
                        onSubmit={handleScatterplotSubmit}
                      />
                    </Box>
                    <Box sx={{ textAlign: 'center', my: 4 }}>
                      <Loading isLoading={scatterplotLoading} />
                    </Box>

                    {showScatterplotChart && (
                      <Box
                        sx={{
                          outline: '1px solid var(--alt-black)',
                          borderRadius: 2,
                          mt: 4,
                        }}
                      >
                        <Grid container alignItems="center" spacing={2} sx={{ my: 2 }}>
                          <Grid item xs={0} sm={2}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setScatterplotPlotOptionsOpen(true)}
                              sx={{ ml: 2 }}
                              startIcon={<Settings />}
                            >
                              Options
                            </Button>
                          </Grid>
                          <Grid item xs={12} sm={8} textAlign="center">
                            <h2 style={{ margin: 0 }}>
                              {scatterplotData?.plotProperties?.plotTitle || 'Scatterplot Chart'}
                                  {patientId && ` for Patient ${patientId}`}
                              {scatterplotDataScope === 'my_program' && ` in Program ${programId}`}
                              {scatterplotDataScope === 'all_programs' && ' in All Programs'}
                            </h2>
                            {scatterplotData?.plotProperties?.plotSubtitle && (
                              <div style={{ fontSize: '1rem', color: '#666', marginTop: 4 }}>
                                {scatterplotSubtitle}
                              </div>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={12} md={2} textAlign="right">
                            {!canExportScatterplot && (
                              <Tooltip title="No data to export" placement="top" arrow>
                                <InfoOutlined
                                  tabIndex={0}
                                  role="img"
                                  aria-label="No data to export"
                                  sx={{ fontSize: '1.1rem', color: 'var(--info-color)', cursor: 'default', verticalAlign: 'middle', mr: 1 }}
                                />
                              </Tooltip>
                            )}
                            <Button
                              sx={{ mr: 3 }}
                              variant="outlined"
                              startIcon={<FileDownload />}
                              onClick={() => scatterplotRef.current?.exportAsPNG()}
                              disabled={!canExportScatterplot}
                              aria-label="Export chart as PNG"
                            >
                              Export
                            </Button>
                          </Grid>
                        </Grid>
                        <Box sx={{ padding: '20px'}}>
                          <ScatterplotChart
                            ref={scatterplotRef}
                            data={scatterplotData}
                            subtitle={scatterplotSubtitle}
                                showCutoffLines={showScatterplotCutoffLines}
                                showRegressionLine={showScatterplotRegressionLine}
                                showDensityCloud={showScatterplotDensityCloud}
                                displayTitle={scatterplotDisplayTitle}
                          />                        
                        </Box>
                      </Box>
                    )}
                  </>
                )}
                {hasSelectedTestResult && selectedDataView === 'single_analyte_analysis' && (
                  <>
                    <PatientDetailsSingleAnalyteAnalysisSelector
                      classes={muiInputClasses}
                      categoryOptions={categoryOptions}
                      analyteOptions={filteredAnalyteOptions}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                      selectedAnalyte={selectedAnalyteToPlot}
                      setSelectedAnalyte={setSelectedAnalyteToPlot}
                      onSubmit={handleSingleAnalyteAnalysisSubmit}
                    />
                    <Box sx={{ textAlign: 'center', my: 4 }}>
                      <Loading isLoading={histogramLoading} />
                    </Box>
                    {showHistogramChart && (
                      <Box
                        sx={{
                          outline: '1px solid var(--alt-black)',
                          borderRadius: 2,
                          mt: 4,
                        }}
                      >
                        <Grid container alignItems="center" spacing={2} sx={{ my: 2 }}>
                          <Grid item xs={0} sm={2}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setHistogramPlotOptionsOpen(true)}
                              sx={{ ml: 2 }}
                              startIcon={<Settings />}
                            >
                              Options
                            </Button>
                          </Grid>
                          <Grid item xs={12} sm={8} textAlign="center">
                            <h2 style={{ margin: 0 }}>
                              {cleanHistogramTitle}
                              {patientId && ` for Patient ${patientId}`}
                              {histogramDataScope === 'my_program' && ` in Program ${programId}`}
                              {histogramDataScope === 'all_programs' && ' in All Programs'}
                            </h2>
                          </Grid>
                          <Grid item xs={12} sm={12} md={2} textAlign="right">
                            <Button
                              sx={{ mr: 3 }}
                              variant="outlined"
                              startIcon={<FileDownload />}
                              onClick={() => histogramRef.current?.exportAsPNG()}
                            >
                              Export
                            </Button>
                          </Grid>
                        </Grid>
                        <Box sx={{ padding: '20px'}}>
                          <HistogramChart
                            ref={histogramRef}
                            data={histogramData}
                            yAxisLabel="Number of Patients"
                            showCutoffLines={showHistogramCutoffLines}
                            cleanHistogramTitle={cleanHistogramTitle}
                          />
                        </Box>
                      </Box>
                    )}
                  </>
                )}
                    </>
                  );
                })()}
              </Grid>
            </Grid>
            : 
            'No patient biochemical data'
            )
          : 
          <div className="comingSoonBanner">Coming Soon</div>
        ))}
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
        {loading ? null : (
          variants && variants.length > 0 ? (
            <PatientDetailsMolecularTable
              data={variants}
              programId={programId}
              breadCrumbs={breadCrumbs}
              assignedByVariantId={assignedByVariantId}
              sortTableByDateDesc={true}
            />
          ) : (
            'No patient variant data'
          )
        )}
      </TabPanel>
      <TabPanel value={selectedTab} index={2}>
        <div className="comingSoonBanner">Coming Soon</div>
      </TabPanel>

      {/* Plot Options Dialog for Scatterplot */}
      <PlotOptionsDialog
        open={scatterplotPlotOptionsOpen}
        onClose={() => setScatterplotPlotOptionsOpen(false)}
        onSave={handleScatterplotPlotOptionsSave}
        currentDataScope={scatterplotDataScope}
        showTestResultSelector={true}
        testResultOptions={options}
        currentTestResult={selectedValue}
        showCutoffLinesToggle={true}
        currentShowCutoffLines={showScatterplotCutoffLines}
        hasCutoffData={hasScatterplotCutoffData}
        cutoffDisabledMessage={scatterplotCutoffDisabledMessage}
        showRegressionLineToggle={true}
        currentShowRegressionLine={showScatterplotRegressionLine}
        hasRegressionData={hasScatterplotRegressionData}
        regressionDisabledMessage={scatterplotRegressionDisabledMessage}
        showDensityCloudToggle={true}
        currentShowDensityCloud={showScatterplotDensityCloud}
        hasDensityData={hasScatterplotDensityData}
        densityDisabledMessage={scatterplotDensityDisabledMessage}
      />

      {/* Plot Options Dialog for Histogram */}
      <PlotOptionsDialog
        open={histogramPlotOptionsOpen}
        onClose={() => setHistogramPlotOptionsOpen(false)}
        onSave={handleHistogramPlotOptionsSave}
        currentDataScope={histogramDataScope}
        showTestResultSelector={true}
        testResultOptions={[]}
        currentTestResult={null}
        showCutoffLinesToggle={true}
        currentShowCutoffLines={showHistogramCutoffLines}
        hasCutoffData={hasCutoffData}
      />
    </div>
  );
};

export default PatientDetails;